/**
 * XDC Network API Service
 *
 * Provides access to XDC account data via the XDCScan API.
 * XDC is EVM-compatible.
 *
 * Documentation: https://xdc.blocksscan.io/docs
 */

import { logger } from './logger';
import { callCoinGeckoAPI } from './api';
import type {
  XDCAccount,
  XDCTransaction,
  XDCMasternode,
  XDCToken,
  XDCWalletData,
  XDCTokenWithInfo,
  XDCApiResponse,
} from '../types/xdc.types';

// XDC APIs
const XDCSCAN_API_URL = 'https://xdc.blocksscan.io/api';
const XDC_RPC_URL = 'https://erpc.xinfin.network';

// Wei to XDC conversion (1 XDC = 10^18 wei)
const WEI_TO_XDC = 1e18;

// Request timeout in ms
const REQUEST_TIMEOUT_MS = 15000;

// Rate limiting
const REQUEST_DELAY_MS = 100;

/**
 * Delay helper for rate limiting
 */
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Fetch with timeout
 */
const fetchWithTimeout = async (url: string, options?: RequestInit): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...options?.headers,
      }
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

/**
 * Convert wei to XDC
 */
export const weiToXdc = (wei: string | number): number => {
  return Number(wei) / WEI_TO_XDC;
};

/**
 * Validate XDC address format
 * XDC addresses start with 'xdc' or '0x' (EVM compatible)
 */
export const isValidXdcAddress = (address: string): boolean => {
  // Convert xdc prefix to 0x for validation
  const normalizedAddress = address.toLowerCase().startsWith('xdc')
    ? '0x' + address.slice(3)
    : address;
  return /^0x[a-fA-F0-9]{40}$/.test(normalizedAddress);
};

/**
 * Normalize XDC address (convert xdc to 0x format)
 */
export const normalizeXdcAddress = (address: string): string => {
  if (address.toLowerCase().startsWith('xdc')) {
    return '0x' + address.slice(3);
  }
  return address;
};

/**
 * Format address with xdc prefix
 */
export const toXdcFormat = (address: string): string => {
  if (address.toLowerCase().startsWith('0x')) {
    return 'xdc' + address.slice(2);
  }
  return address;
};

/**
 * XDC RPC call
 */
const rpcCall = async (method: string, params: any[]): Promise<any> => {
  const response = await fetchWithTimeout(XDC_RPC_URL, {
    method: 'POST',
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 1,
      method,
      params,
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const data = await response.json();
  if (data.error) {
    throw new Error(data.error.message);
  }

  return data.result;
};

/**
 * XDC API Service
 */
export const xdcAPI = {
  /**
   * Get account balance via RPC
   */
  async getBalance(address: string): Promise<string> {
    try {
      const normalizedAddress = normalizeXdcAddress(address);
      const balance = await rpcCall('eth_getBalance', [normalizedAddress, 'latest']);
      return balance;
    } catch (error) {
      logger.error('XdcAPI', `Failed to fetch balance for ${address}`, error);
      return '0x0';
    }
  },

  /**
   * Get account transactions via XDCScan API
   */
  async getTransactions(
    address: string,
    limit: number = 50
  ): Promise<XDCTransaction[]> {
    try {
      const normalizedAddress = normalizeXdcAddress(address);
      const response = await fetchWithTimeout(
        `${XDCSCAN_API_URL}?module=account&action=txlist&address=${normalizedAddress}&page=1&offset=${limit}&sort=desc`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: XDCApiResponse<XDCTransaction[]> = await response.json();
      if (data.status !== '1') {
        return [];
      }

      return data.result || [];
    } catch (error) {
      logger.error('XdcAPI', `Failed to fetch transactions for ${address}`, error);
      return [];
    }
  },

  /**
   * Get token balances via XDCScan API
   */
  async getTokenBalances(address: string): Promise<XDCTokenWithInfo[]> {
    try {
      const normalizedAddress = normalizeXdcAddress(address);
      const response = await fetchWithTimeout(
        `${XDCSCAN_API_URL}?module=account&action=tokenlist&address=${normalizedAddress}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: XDCApiResponse<any[]> = await response.json();
      if (data.status !== '1' || !data.result) {
        return [];
      }

      return data.result.map((token: any) => ({
        address: token.contractAddress,
        name: token.name || 'Unknown',
        symbol: token.symbol || '',
        balance: Number(token.balance) / Math.pow(10, Number(token.decimals) || 18),
        decimals: Number(token.decimals) || 18,
      }));
    } catch (error) {
      logger.error('XdcAPI', `Failed to fetch tokens for ${address}`, error);
      return [];
    }
  },

  /**
   * Get masternode status
   */
  async getMasternodeStatus(address: string): Promise<XDCMasternode | null> {
    try {
      // XDC masternode contract query
      // For now, return null as masternode querying requires more complex contract calls
      return null;
    } catch (error) {
      logger.error('XdcAPI', `Failed to fetch masternode status for ${address}`, error);
      return null;
    }
  },

  /**
   * Get XDC price from CoinGecko
   */
  async getXdcPrice(): Promise<number> {
    try {
      const data = await callCoinGeckoAPI('/simple/price', {
        ids: 'xdce-crowd-sale',
        vs_currencies: 'usd'
      });
      return data['xdce-crowd-sale']?.usd || 0;
    } catch (error) {
      logger.error('XdcAPI', 'Failed to fetch XDC price', error);
      return 0;
    }
  },

  /**
   * Get complete wallet data for an address
   */
  async getWalletData(address: string, xdcPrice: number): Promise<XDCWalletData | null> {
    try {
      if (!isValidXdcAddress(address)) {
        logger.warn('XdcAPI', `Invalid address format: ${address}`);
        return null;
      }

      // Fetch balance
      const balanceHex = await this.getBalance(address);
      const balance = weiToXdc(parseInt(balanceHex, 16));
      await delay(REQUEST_DELAY_MS);

      // Fetch transactions
      const transactions = await this.getTransactions(address, 25);
      await delay(REQUEST_DELAY_MS);

      // Fetch tokens
      const tokens = await this.getTokenBalances(address);
      await delay(REQUEST_DELAY_MS);

      // Check masternode status
      const masternode = await this.getMasternodeStatus(address);

      return {
        address: toXdcFormat(address),
        balance,
        balanceUsd: balance * xdcPrice,
        stakedAmount: 0, // TODO: Implement staking query
        stakedAmountUsd: 0,
        pendingRewards: 0,
        pendingRewardsUsd: 0,
        isMasternode: !!masternode,
        masternodeStatus: masternode?.status,
        votedFor: null,
        tokens,
        recentTransactions: transactions,
        lastUpdated: Date.now(),
      };
    } catch (error) {
      logger.error('XdcAPI', `Failed to get wallet data for ${address}`, error);
      return null;
    }
  },

  /**
   * Get aggregated data for multiple addresses
   */
  async getMultipleWalletsData(
    addresses: string[],
    xdcPrice: number
  ): Promise<XDCWalletData[]> {
    const results: XDCWalletData[] = [];

    for (const address of addresses) {
      const walletData = await this.getWalletData(address, xdcPrice);
      if (walletData) {
        results.push(walletData);
      }
      await delay(500);
    }

    return results;
  },

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp: number): Date {
    return new Date(timestamp * 1000);
  }
};

export default xdcAPI;
